import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import InactivityLogout from "@/components/InactivityLogout";
import { Toaster } from "@/components/ui/sonner";
import { Mailbox } from "lucide-react";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import NewMigration from "@/pages/NewMigration";
import BulkMigration from "@/pages/BulkMigration";
import CloudMigration from "@/pages/CloudMigration";
import MigrationDetail from "@/pages/MigrationDetail";
import Settings from "@/pages/Settings";
import Pricing from "@/pages/Pricing";
import Admin from "@/pages/Admin";

const protectedPage = (Page) => (
  <ProtectedRoute>
    <Layout>
      <Page />
    </Layout>
  </ProtectedRoute>
);

function AppLoadingScreen() {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-5 bg-background">
      <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-secondary text-white shadow-[3px_3px_8px_hsl(216_23%_50%/0.3),-2px_-2px_6px_hsl(0_0%_100%/0.1)]">
        <Mailbox className="h-8 w-8 animate-pulse" />
        <span className="absolute inset-0 animate-ping rounded-2xl border-2 border-primary/40" />
      </div>
      <div className="text-center">
        <p className="font-heading text-lg font-bold tracking-tighter">MailShift</p>
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Loading…</p>
      </div>
    </div>
  );
}

function RootRoute() {
  const { user } = useAuth();
  if (user === null) {
    return <AppLoadingScreen />;
  }
  if (user === false) return <Landing />;
  return (
    <Layout>
      <Dashboard />
    </Layout>
  );
}

function App() {
  return (
    <div className="App">
      <SettingsProvider>
        <AuthProvider>
          <BrowserRouter>
            <InactivityLogout />
            <Routes>
              <Route path="/" element={<RootRoute />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/register" element={<Register />} />
              <Route path="/new" element={protectedPage(NewMigration)} />
              <Route path="/bulk" element={protectedPage(BulkMigration)} />
              <Route path="/cloud" element={protectedPage(CloudMigration)} />
              <Route path="/pricing" element={protectedPage(Pricing)} />
              <Route path="/settings" element={protectedPage(Settings)} />
              <Route path="/admin" element={protectedPage(Admin)} />
              <Route path="/migrations/:id" element={protectedPage(MigrationDetail)} />
            </Routes>
          </BrowserRouter>
          <Toaster position="bottom-right" richColors />
        </AuthProvider>
      </SettingsProvider>
    </div>
  );
}

export default App;

